// IE Hub - Theme Token Resolver
// Handles merging of base tokens with client-specific overrides

import { createTheme, type Theme } from '../../ui';
import { baseTheme } from '../defaults/index';
import { formTokens } from '../defaults/form';
import { alertTokens } from '../defaults/alert';

// Type definitions
interface ThemeOverrides {
  palette?: any;
  typography?: any;
  spacing?: any;
  breakpoints?: any;
  shape?: any;
  components?: Record<string, any>;
  shadows?: any;
  transitions?: any;
  zIndex?: any;
  mixins?: any;
}

interface IeHubTokens {
  form: any;
  alert: any;
  client: string;
  version: string;
  error?: string;
}

interface ExtendedTheme extends Theme {
  ieHub?: IeHubTokens;
}

/**
 * Deep merge utility for theme objects
 * Properly handles nested objects and arrays
 */
function deepMerge(target: any, source: any): any {
  if (!source || typeof source !== 'object') return target;
  if (!target || typeof target !== 'object') return source;

  const result = { ...target };

  Object.keys(source).forEach((key) => {
    const srcVal = source[key];
    const tgtVal = result[key];
    if (srcVal && typeof srcVal === 'object' && !Array.isArray(srcVal)) {
      result[key] = deepMerge((tgtVal as Record<string, unknown>) || {}, srcVal);
    } else {
      result[key] = srcVal;
    }
  });

  return result;
}

/**
 * Validates theme override structure
 * Ensures overrides follow MUI theme schema
 */
function validateThemeOverrides(overrides: any): ThemeOverrides {
  if (!overrides || typeof overrides !== 'object') return {} as ThemeOverrides;

  const validKeys = [
    'palette', 'typography', 'spacing', 'breakpoints', 'shape',
    'components', 'shadows', 'transitions', 'zIndex', 'mixins'
  ];

  const validated: ThemeOverrides = {};
  validKeys.forEach((key) => {
    if ((overrides as Record<string, unknown>)[key] !== undefined) {
      (validated as Record<string, unknown>)[key] = (overrides as Record<string, unknown>)[key];
    }
  });

  return validated;
}

/**
 * Sanitizes component overrides to prevent dangerous injections
 * Only allows safe CSS properties and values
 */
function sanitizeComponentOverrides(components: any): any {
  if (!components || typeof components !== 'object') return {};

  // List of allowed CSS properties for security
  const allowedCSSProps = [
    'color', 'backgroundColor', 'borderColor', 'borderRadius', 'borderWidth',
    'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
    'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'letterSpacing',
    'textAlign', 'textTransform', 'textDecoration',
    'width', 'maxWidth', 'height', 'maxHeight', 'minWidth', 'minHeight',
    'display', 'flexDirection', 'justifyContent', 'alignItems', 'gap',
    'boxShadow', 'opacity', 'zIndex', 'position', 'top', 'right', 'bottom', 'left'
  ];

  function sanitizeStyleObject(styles: any): any {
    if (!styles || typeof styles !== 'object') return styles;

    const sanitized: any = {};
    Object.keys(styles).forEach((prop) => {
      // Allow MUI system props and whitelisted CSS props
      if (allowedCSSProps.includes(prop) || prop.startsWith('&') || prop.startsWith('@')) {
        const val = styles[prop];
        if (typeof val === 'object') {
          sanitized[prop] = sanitizeStyleObject(val);
        } else if (typeof val === 'string' || typeof val === 'number') {
          sanitized[prop] = val;
        }
      }
    });
    return sanitized;
  }

  const sanitized: any = {};
  Object.keys(components).forEach((componentName) => {
    if (componentName.startsWith('Mui')) { // Only allow MUI components
      const component = components[componentName];
      sanitized[componentName] = {} as any;

      if (component.defaultProps) {
        sanitized[componentName].defaultProps = component.defaultProps;
      }

      if (component.styleOverrides) {
        sanitized[componentName].styleOverrides = {};
        Object.keys(component.styleOverrides).forEach((slot) => {
          sanitized[componentName].styleOverrides[slot] = sanitizeStyleObject(
            component.styleOverrides[slot]
          );
        });
      }

      if (component.variants) {
        sanitized[componentName].variants = component.variants;
      }
    }
  });

  return sanitized;
}

/**
 * Merges base theme with client-specific overrides
 * Precedence: base < client < schema overrides
 */
export function mergeTokens(clientThemeOverrides: ThemeOverrides = {}, schemaOverrides: ThemeOverrides = {}): ExtendedTheme {
  try {
    // Validate and sanitize inputs
    const validatedClientOverrides = validateThemeOverrides(clientThemeOverrides);
    const validatedSchemaOverrides = validateThemeOverrides(schemaOverrides);
    
    // Sanitize component overrides
    if (validatedClientOverrides.components) {
      validatedClientOverrides.components = sanitizeComponentOverrides(
        validatedClientOverrides.components
      );
    }
    
    if (validatedSchemaOverrides.components) {
      validatedSchemaOverrides.components = sanitizeComponentOverrides(
        validatedSchemaOverrides.components
      );
    }
    
    // Merge in order of precedence: base < client < schema
    const mergedTheme = deepMerge(
      baseTheme,
      deepMerge(validatedClientOverrides, validatedSchemaOverrides)
    );
    
    // Create final MUI theme
    const finalTheme = createTheme(mergedTheme as any) as ExtendedTheme;
    
    // Attach custom tokens for use by engine
    finalTheme.ieHub = {
      form: formTokens,
      alert: alertTokens,
      client: (import.meta as any).env?.VITE_CLIENT || 'default',
      version: '1.0.0'
    };
    
    return finalTheme;
    
  } catch (error) {
    console.error('Error merging theme tokens:', error);
    
    // Fallback to base theme with custom tokens
    const fallbackTheme = createTheme(baseTheme as any) as ExtendedTheme;
    fallbackTheme.ieHub = {
      form: formTokens,
      alert: alertTokens,
      client: 'default',
      version: '1.0.0',
      error: (error as Error).message
    };
    
    return fallbackTheme;
  }
}

/**
 * Gets the current client theme configuration
 */
export async function getClientTheme(): Promise<ThemeOverrides> {
  // Deprecated: client theme overrides should be bundled; keep for backward compat
  try {
    const { themeOverrides } = await import('@config/theme');
    return themeOverrides || {};
  } catch (_) {
    return {};
  }
}

/**
 * Creates a scoped theme for specific sections or components
 */
export function createScopedTheme(baseTheme: Theme, scopedOverrides: ThemeOverrides): Theme {
  const sanitizedOverrides = validateThemeOverrides(scopedOverrides);
  return createTheme(deepMerge(baseTheme, sanitizedOverrides));
}

export default mergeTokens;
